import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { taskKeys } from '../tasks/hooks';
import { updateTask } from '../tasks/queries';
import type { ProjectTasks, Task, TaskUpdate } from '../tasks/types';
import {
  createMyDaySection,
  deleteMyDaySection,
  fetchMyDaySections,
  moveMyDaySections,
  myDaySectionKeys,
  renameMyDaySection,
} from './sectionQueries';

/**
 * Placing tasks on My Day, optimistically.
 *
 * A drag that lands and then snaps back for a round trip reads as the drop
 * having failed. So the cached rows are patched the moment the drop happens,
 * the writes follow, and the patch is undone only if the database refuses.
 * Same contract as useReorderTasks; the cache is the project's task list, so
 * every other page reading it sees the same row.
 *
 * Reads and every other write go through the tasks hooks unchanged. My Day is
 * the personal project seen through a different lens, not a second store.
 *
 * Every My Day position write runs in ONE queue (scope 'my-day-order'): two
 * quick moves often write the same rows, and move 1's write landing after
 * move 2's would save the old slot. A queued move still patches the cache at
 * once (TanStack runs onMutate before it waits its turn), so the patch comes
 * BEFORE the first await: the next key press reads the moved order. Only the
 * last move in the queue refetches, so a settled move does not briefly draw
 * the saved order without the moves still waiting behind it.
 */
export type Place = { taskId: string; changes: TaskUpdate };

const MY_DAY_ORDER = ['my-day-order'] as const;

function usePlaceMutation<V>(projectId: string, placesOf: (input: V) => Place[]) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: MY_DAY_ORDER,
    scope: { id: 'my-day-order' },
    mutationFn: async (input: V) => {
      // One at a time and in order, like reorderTasks: the first write is the
      // moved task, and a failure stops the rest (onError puts the screen back).
      for (const { taskId, changes } of placesOf(input)) {
        await updateTask(client, taskId, changes);
      }
    },
    onMutate: async (input: V) => {
      const key = taskKeys.project(projectId);
      const previous = queryClient.getQueryData<ProjectTasks>(key);
      if (previous) {
        // updated_at moves too: the merge orders by it, and the database will
        // stamp each written row on the way through.
        const stamp = new Date().toISOString();
        const changesById = new Map(
          placesOf(input).map((place) => [place.taskId, place.changes] as const),
        );
        queryClient.setQueryData<ProjectTasks>(key, {
          ...previous,
          tasks: previous.tasks.map((task): Task => {
            const changes = changesById.get(task.id);
            return changes === undefined ? task : { ...task, ...changes, updated_at: stamp };
          }),
        });
      }
      // After the patch, never before it: cancelling waits for an in-flight
      // fetch to unwind, and a press in that window would read the old order.
      // A cancelled fetch reverts to the patched data (setQueryData is kept as
      // the revert state), so the patch survives.
      await queryClient.cancelQueries({ queryKey: key });
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) {
        queryClient.setQueryData(taskKeys.project(projectId), context.previous);
      }
    },
    onSettled: () => {
      // The settling move still counts as pending here, so 1 means "last".
      if (queryClient.isMutating({ mutationKey: MY_DAY_ORDER }) === 1) {
        void queryClient.invalidateQueries({ queryKey: taskKeys.project(projectId) });
      }
    },
  });
}

/** One task: a slot release, a group change. Runs in the same queue. */
export function usePlaceTask(projectId: string) {
  return usePlaceMutation(projectId, (place: Place) => [place]);
}

/** A move: the moved task's placement first, then any neighbour's slot. */
export function usePlaceTasks(projectId: string) {
  return usePlaceMutation(projectId, (places: Place[]) => places);
}

function useMe(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

export function useMyDaySections() {
  const { client, access } = useAuth();
  return useQuery({
    queryKey: myDaySectionKeys.all,
    queryFn: () => fetchMyDaySections(client),
    enabled: access.status === 'approved',
    retry: false,
  });
}

export function useAddMyDaySection() {
  const { client } = useAuth();
  const userId = useMe();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; position: number }) =>
      createMyDaySection(client, { ...input, userId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myDaySectionKeys.all });
    },
  });
}

export function useRenameMyDaySection() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; name: string }) => renameMyDaySection(client, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myDaySectionKeys.all });
    },
  });
}

export function useMoveMyDaySections() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (moves: { id: string; position: number }[]) => moveMyDaySections(client, moves),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myDaySectionKeys.all });
    },
  });
}

/**
 * Removing a section.
 *
 * The task list is invalidated as well as the section list, and it has to be:
 * the database just set my_day_section_id to null on every task that was in
 * there, and the cached rows still say otherwise. Without this the tasks would
 * vanish from the page until something else happened to refetch them, which
 * looks exactly like the deletion having taken the tasks with it.
 */
export function useDeleteMyDaySection(projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMyDaySection(client, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: myDaySectionKeys.all });
      void queryClient.invalidateQueries({ queryKey: taskKeys.project(projectId) });
    },
  });
}
